import { useDebounceFn, useMemoizedFn, useUpdateEffect } from 'ahooks';
import { type MutableRefObject, type RefObject, useEffect, useRef } from 'react';
import { applyHighlightsToDom, mountTextLayerToDom, updateSelectedHighlight } from '../utils/pdf-text-layer';

export interface UseTextLayerManagerProps {
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js API
    pdfDoc: any;
    totalPages: number;
    scale: number;
    currentPage?: number;
    renderPixelRadio?: number;
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
    getCachedPage: (pageNum: number) => Promise<any | null>;
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js text content
    getCachedTextContent: (pageNum: number) => Promise<any | null>;
    /** 当前搜索词 ref（由 usePdfSearch 所有，只读） */
    searchTermRef: MutableRefObject<string>;
    /** 当前选中匹配位置 ref（由 usePdfSearch 所有，只读） */
    activeMatchRef: MutableRefObject<{ page: number; index: number } | null>;
}

/**
 * 文字层管理 Hook
 *
 * 从 usePdfSearch 拆分：负责文字层的挂载、注册、显隐、高亮及缩放重绘。
 * 不持有搜索词状态，通过 ref 参数读取最新值。
 */
export function useTextLayerManager(props: UseTextLayerManagerProps) {
    const {
        pdfDoc,
        totalPages,
        scale,
        currentPage,
        renderPixelRadio,
        getCachedPage,
        getCachedTextContent,
        searchTermRef,
        activeMatchRef,
    } = props;

    const textLayerRefsRef = useRef<Map<number, RefObject<HTMLDivElement>>>(new Map());
    const prevScaleRef = useRef<number | undefined>(scale);
    const scaleRef = useRef<number>(scale);
    const renderPixelRadioRef = useRef<number | undefined>(renderPixelRadio);
    const rerenderingPagesRef = useRef<Set<number>>(new Set());
    const visiblePagesRef = useRef<Set<number>>(new Set());
    const renderCycleIdRef = useRef(0);
    const latestRerenderCycleIdRef = useRef(0);

    const isValidPageNum = useMemoizedFn((pageNum: number): boolean => {
        return pageNum >= 1 && pageNum <= totalPages;
    });

    const clearAllHighlights = useMemoizedFn(() => {
        for (const ref of Array.from(textLayerRefsRef.current.values())) {
            if (ref.current) {
                applyHighlightsToDom(ref.current, '');
            }
        }
    });

    const hideTextLayer = useMemoizedFn((pageNum: number) => {
        const ref = textLayerRefsRef.current.get(pageNum);
        if (ref?.current) {
            ref.current.style.transition = 'none';
            ref.current.style.opacity = '0';
        }
    });

    const showTextLayer = useMemoizedFn((pageNum: number) => {
        const ref = textLayerRefsRef.current.get(pageNum);
        if (!ref?.current) return;
        ref.current.style.opacity = '1';
        ref.current.style.transition = 'opacity 0.5s ease-in';
    });

    const hideAllTextLayers = useMemoizedFn(() => {
        const visiblePages = visiblePagesRef.current;
        if (visiblePages.size > 0) {
            for (const pageNum of Array.from(visiblePages)) hideTextLayer(pageNum);
            return;
        }
        for (const pageNum of Array.from(textLayerRefsRef.current.keys())) hideTextLayer(pageNum);
    });

    const isCycleStale = useMemoizedFn((cycleId: number | undefined): boolean => {
        return cycleId !== undefined && cycleId !== renderCycleIdRef.current;
    });

    /** 挂载文字层并应用高亮（forceRerender 分支） */
    const mountAndHighlight = useMemoizedFn(
        async (
            container: HTMLDivElement,
            pageNum: number,
            searchTerm: string,
            selectedMatchIndex: number | undefined,
            cycleId: number | undefined
        ): Promise<void> => {
            const pageObj = await getCachedPage(pageNum);
            if (isCycleStale(cycleId) || !pageObj) return;
            const textContent = await getCachedTextContent(pageNum);
            if (isCycleStale(cycleId) || !textContent) return;
            await mountTextLayerToDom(container, pageObj, textContent, scaleRef.current, renderPixelRadioRef.current);
            if (isCycleStale(cycleId)) return;
            applyHighlightsToDom(container, searchTerm, { selectedMatchIndex });
        }
    );

    const highlightPage = useMemoizedFn(async (pageNum: number, forceRerender = false, cycleId?: number) => {
        if (isCycleStale(cycleId)) return;
        const textLayerRef = textLayerRefsRef.current.get(pageNum);
        if (!(pdfDoc && textLayerRef?.current && isValidPageNum(pageNum))) return;

        const container = textLayerRef.current;
        const searchTerm = searchTermRef.current;
        const activeMatch = activeMatchRef.current;
        const selectedMatchIndex = activeMatch?.page === pageNum ? activeMatch.index : undefined;

        if (container.childElementCount === 0 || forceRerender) {
            try {
                await mountAndHighlight(container, pageNum, searchTerm, selectedMatchIndex, cycleId);
            } catch (_error) {
                // 静默处理
            }
        } else {
            const lastAppliedTerm = container.getAttribute('data-applied-search-term');
            if (lastAppliedTerm === searchTerm) {
                updateSelectedHighlight(container, selectedMatchIndex);
            } else {
                applyHighlightsToDom(container, searchTerm, { selectedMatchIndex });
            }
        }
    });

    const safeHighlightPage = useMemoizedFn((pageNum: number, cycleId?: number): Promise<void> => {
        return highlightPage(pageNum, false, cycleId);
    });

    const collectVisiblePages = useMemoizedFn((): number[] => {
        const visiblePages = visiblePagesRef.current;
        const pageSet = new Set<number>();
        if (visiblePages?.size > 0) {
            for (const pageNum of Array.from(visiblePages)) {
                if (isValidPageNum(pageNum)) pageSet.add(pageNum);
            }
        }
        if (currentPage && isValidPageNum(currentPage) && !pageSet.has(currentPage)) {
            pageSet.add(currentPage);
        }
        return Array.from(pageSet);
    });

    const highlightVisiblePages = useMemoizedFn(async (pageList: number[]): Promise<void> => {
        if (pageList.length === 0) return;
        await Promise.all(pageList.map((pageNum) => safeHighlightPage(pageNum)));
        for (const pageNum of pageList) showTextLayer(pageNum);
    });

    const rerenderAllTextLayers = useMemoizedFn(async (cycleId?: number) => {
        if (!scale || scale <= 0) return;
        if (cycleId !== undefined && cycleId !== renderCycleIdRef.current) return;

        const rerenderPromises: Promise<void>[] = [];
        const pageNums: number[] = [];

        try {
            const visiblePages = visiblePagesRef.current;
            const collect = (pageNum: number) => {
                const ref = textLayerRefsRef.current.get(pageNum);
                if (ref?.current && ref.current.childElementCount > 0) {
                    rerenderingPagesRef.current.add(pageNum);
                    pageNums.push(pageNum);
                }
            };

            if (visiblePages.size > 0) {
                for (const pageNum of Array.from(visiblePages)) collect(pageNum);
            } else {
                for (const pageNum of Array.from(textLayerRefsRef.current.keys())) collect(pageNum);
            }

            await new Promise((resolve) => requestAnimationFrame(resolve));
            if (cycleId !== undefined && cycleId !== renderCycleIdRef.current) return;

            for (const pageNum of pageNums) {
                rerenderPromises.push(highlightPage(pageNum, true, cycleId));
            }

            await Promise.all(rerenderPromises).catch(() => {
                // 静默处理
            });
            if (cycleId !== undefined && cycleId !== renderCycleIdRef.current) return;
            for (const pageNum of pageNums) showTextLayer(pageNum);
        } finally {
            rerenderingPagesRef.current.clear();
        }
    });

    const { run: debouncedRerenderAllTextLayers } = useDebounceFn(
        () => {
            const cycleId = latestRerenderCycleIdRef.current;
            void rerenderAllTextLayers(cycleId).catch(() => {
                // 静默处理
            });
        },
        { wait: 150 }
    );

    const registerTextLayer = useMemoizedFn((pageNum: number, textLayerRef: RefObject<HTMLDivElement>) => {
        textLayerRefsRef.current.set(pageNum, textLayerRef);
        if (!(searchTermRef.current && textLayerRef.current)) return;
        if (rerenderingPagesRef.current.has(pageNum)) return;

        hideTextLayer(pageNum);
        const cycleId = renderCycleIdRef.current;
        requestAnimationFrame(() => {
            if (rerenderingPagesRef.current.has(pageNum)) return;
            setTimeout(() => {
                if (rerenderingPagesRef.current.has(pageNum)) return;
                if (cycleId !== renderCycleIdRef.current) return;
                // 使用 forceRerender=true 强制重新挂载文本层。
                // 此回调由 PdfPage.onRenderSuccess 在每次 canvas 渲染完成后触发：
                // canvas 尺寸可能已变化（缩放/重渲染），文本层需要重新计算定位以匹配。
                // 如果前一次的防抖重渲染 (debouncedRerenderAllTextLayers) 已在旧 canvas
                // 尺寸下挂载了文本层，forceRerender 确保此处用最新 canvas 尺寸覆盖。
                highlightPage(pageNum, true, cycleId)
                    .then(() => {
                        if (rerenderingPagesRef.current.has(pageNum)) return;
                        if (cycleId !== renderCycleIdRef.current) return;
                        showTextLayer(pageNum);
                    })
                    .catch(() => {
                        // 静默处理
                    });
            }, 0);
        });
    });

    const unregisterTextLayer = useMemoizedFn((pageNum: number) => {
        textLayerRefsRef.current.delete(pageNum);
    });

    const updateVisiblePages = useMemoizedFn((pages: number[]) => {
        const next = new Set<number>();
        for (const p of pages) {
            if (typeof p === 'number' && p >= 1 && p <= totalPages) {
                next.add(p);
            }
        }
        visiblePagesRef.current = next;
    });

    // 同步 renderPixelRadio 到 ref
    useEffect(() => {
        renderPixelRadioRef.current = renderPixelRadio;
    }, [renderPixelRadio]);

    // 监听当前页面变化，自动高亮
    useUpdateEffect(() => {
        if (currentPage && searchTermRef.current) {
            const cycleId = ++renderCycleIdRef.current;
            hideTextLayer(currentPage);
            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (cycleId !== renderCycleIdRef.current) return;
                    highlightPage(currentPage, false, cycleId)
                        .then(() => {
                            if (cycleId !== renderCycleIdRef.current) return;
                            showTextLayer(currentPage);
                        })
                        .catch(() => {
                            // 静默处理
                        });
                }, 0);
            });
        }
    }, [currentPage]);

    // 监听缩放变化，重新渲染文本层
    useUpdateEffect(() => {
        if (!scale || scale <= 0) return;
        scaleRef.current = scale;
        renderPixelRadioRef.current = renderPixelRadio;
        if (prevScaleRef.current !== undefined && prevScaleRef.current !== scale) {
            const cycleId = ++renderCycleIdRef.current;
            latestRerenderCycleIdRef.current = cycleId;
            hideAllTextLayers();
            debouncedRerenderAllTextLayers();
        }
        prevScaleRef.current = scale;
    }, [scale, renderPixelRadio]);

    return {
        registerTextLayer,
        unregisterTextLayer,
        hideTextLayer,
        showTextLayer,
        clearAllHighlights,
        highlightPage,
        collectVisiblePages,
        highlightVisiblePages,
        updateVisiblePages,
        renderCycleIdRef,
    };
}
